// Client-side mirror of the server's narrative rules (api/shared/billingConfig/narratives.js
// validateNarrative) so a bad edit is rejected instantly instead of after a round trip. The
// server is still the authority — it re-runs all of this when the change is requested.
//
// All comparisons are string-normalized (stored Idx come back numeric, picklist keys may not).

// ALL covers TIME and DISB, so it conflicts with anything (including another ALL).
export function typesConflict(a, b) {
  const x = String(a || '').toUpperCase();
  const y = String(b || '').toUpperCase();
  return x === y || x === 'ALL' || y === 'ALL';
}

// JOB-level: existing JOB rows that would collide with this entry (same service, a
// conflicting type, and at least one shared job). `excludeUuid` skips the row being edited.
export function jobCollisions(rows, { type, serv, idx, excludeUuid = null }) {
  const want = new Set((idx || []).map(String));
  return (rows || []).filter(n =>
    n.uuid !== excludeUuid &&
    n.Level === 'JOB' &&
    typesConflict(n.Type, type) &&
    String(n.Serv) === String(serv) &&
    (n.Idx || []).some(i => want.has(String(i))));
}

// SERV-level: at most one standard per service + conflicting type. Returns true on a duplicate.
export function servDuplicate(rows, { type, serv, excludeUuid = null }) {
  return (rows || []).some(n =>
    n.uuid !== excludeUuid &&
    n.Level === 'SERV' &&
    String(n.Serv) === String(serv) &&
    typesConflict(n.Type, type));
}

// Plain text of a narrative (same stripping the server uses for its "empty" check).
export const narrativeText = s =>
  String(s ?? '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

// Rows that count for collision checks: everything listed except rows with a pending delete
// (own rows + the parent's inherited group rows), exactly the server's `view.effective`.
export const effectiveRows = rows => (rows || []).filter(r => !r._pendingDelete);

// Does a parent-inherited (locked) row occupy a coordinate this draft would write to? The
// server compares exact (level, type, serv, job) coordinates here — not typesConflict.
function lockedOverlap(rows, { level, type, serv, idx }) {
  const jobs = level === 'JOB' ? (idx || []).map(String) : ['0'];
  return rows.some(e => {
    if (!e._locked || e.Level !== level || e.Type !== type || String(e.Serv) !== String(serv)) return false;
    const ej = e.Level === 'JOB' ? (e.Idx || []).map(String) : ['0'];
    return ej.some(j => jobs.includes(j));
  });
}

// Validate a draft the way validateNarrative does. Returns an error message, or null when OK.
//   draft: { uuid, Level, Idx, Type, Serv, Narrative, ApplyToGroup }
//   ctx:   { rows (view.rows), services, jobs (view lists), prev (row being edited | null),
//            isParent, parentCode, jobLabel(i) }
export function validateDraft(draft, ctx) {
  const { rows, services, jobs, prev, isParent, parentCode, jobLabel } = ctx;
  const level = draft.Level, type = draft.Type, serv = String(draft.Serv || '');
  const idx = (draft.Idx || []).map(Number);

  if (level !== 'JOB' && level !== 'SERV') return 'Please select a Level.';
  if (!type) return 'Please select a Type.';
  if (!serv) return 'Please select a Service.';
  if (level === 'JOB' && idx.length === 0) return 'Please select at least one Job.';
  if (!narrativeText(draft.Narrative)) return 'Narrative cannot be empty.';

  // Services / jobs must be ones this client has (a row being edited may keep an old one).
  const okServ = new Set((services || []).map(s => String(s.key)));
  if (prev) okServ.add(String(prev.Serv));
  if (!okServ.has(serv)) return `${serv} isn't one of this client's services.`;
  if (level === 'JOB') {
    const okJobs = new Set((jobs || []).map(j => Number(j.key)));
    for (const i of (prev?.Idx || [])) okJobs.add(Number(i));
    const bad = idx.filter(i => !okJobs.has(i));
    if (bad.length) return `Job ${bad.join(', ')} isn't one of this client's active jobs.`;
  }

  if (draft.ApplyToGroup && !isParent) return 'Only a billing-group parent can apply a standard to its whole group.';

  const eff = effectiveRows(rows);
  if (lockedOverlap(eff, { level, type, serv, idx })) {
    return `This overlaps a standard inherited from billing parent${parentCode ? ` ${parentCode}` : ''} and can't be overridden here.`;
  }
  if (level === 'JOB') {
    const hits = jobCollisions(eff, { type, serv, idx, excludeUuid: draft.uuid });
    if (hits.length) {
      const want = new Set(idx.map(String));
      const clash = [...new Set(hits.flatMap(n => (n.Idx || []).filter(i => want.has(String(i)))))]
        .map(i => (jobLabel ? jobLabel(i) : `#${i}`));
      return `A narrative already exists for job${clash.length > 1 ? 's' : ''} ${clash.join(', ')} with Service "${serv}" and a conflicting Type ("${type}" overlaps ALL/TIME/DISB).`;
    }
  } else if (servDuplicate(eff, { type, serv, excludeUuid: draft.uuid })) {
    return `A service-level narrative already exists for Service "${serv}" with a conflicting Type ("${type}" overlaps ALL/TIME/DISB).`;
  }
  return null;
}
